import { FC, JSXElementConstructor, ReactElement, useEffect, useState } from 'react';
import { Upload, Modal }                                                from 'antd';
import type { GetProp, UploadFile, UploadProps } from 'antd';
import ImgCrop from 'antd-img-crop';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, PointerSensor, useSensor } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Style from './ImageUploader.style';
import { Icon } from 'View/Common';

type TProps = {
  className?: string,
  onChange?: (fileList: UploadFile[]) => void,
  value?: any,
  defaultValue?: any,
  max?: number,
  quality?: number,
  aspect?: number,
}

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

interface DraggableUploadListItemProps {
  originNode: ReactElement<any, string | JSXElementConstructor<any>>;
  file: UploadFile<any>;
}

const getSrcFromFile = (file: any) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file.originFileObj);
    reader.onload = () => resolve(reader.result);
  });
};

const DraggableUploadListItem = ({ originNode, file }: DraggableUploadListItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: file.uid,
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: 'move',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      // prevent preview event when drag end
      className={isDragging ? 'is-dragging' : ''}
      {...attributes}
      {...listeners}
    >
      {file.status === 'error' && isDragging ? originNode.props.children : originNode}
    </div>
  );
};

export const ImageUploader: FC<TProps> = ({ className = '', aspect, quality, max, onChange, value, defaultValue }) => {

  const [fileList, setFileList] = useState<UploadFile[]>(defaultValue ?? value ?? []);

  const sensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 },
  });

  const [ previewImage, setPreviewImage ] = useState<UploadFile|null>(null);

  useEffect(() => {
    if(fileList.length === 0) {
      return;
    }
    onChange && onChange(fileList.map((file, index) => ({ ...file, sorting: (index + 1) * 100})));
  }, [fileList]);

  useEffect(() => {
    const fileListIds = fileList.map(({ uid }) => uid);
    const newFileListIds = value.map(({ id }: any) => id);
    const compareListIds = [ ...new Set([ ...fileListIds, ...newFileListIds ]) ];

    if(fileListIds.length !== newFileListIds.length && compareListIds.length !== fileListIds.length ) {
      setFileList(value);
    }
  }, [value]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setFileList((prev) => {
        const activeIndex = prev.findIndex((i) => i.uid === active.id);
        const overIndex = prev.findIndex((i) => i.uid === over?.id);
        return arrayMove(prev, activeIndex, overIndex);
      });
    }
  };

  const handleOnRemove = (file: UploadFile) => {
    // @ts-ignore
    if(file.id.match(/^new_/i)) {
      return setFileList((prev) => prev.filter((i) => i.uid !== file.uid));
    }

    setFileList((prev) => prev.map((image) => {
      if(image.uid === file.uid) {
        return { ...image, _delete: true };
      }
      return image;
    }));
  }

  const handleOnBeforeUpload = (file: FileType) => {
    const fileRef = { ...file, name: file.name, aspect: aspect || 1, size: file.size, type: file.type };

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // @ts-ignore
      setFileList((prev) =>([...prev, { ...fileRef, url: reader.result, id: `new_${fileRef.uid}` }]));
    };
    return false;
  }

  const onPreview = async (file: UploadFile) => {
    const src = (file.url || (await getSrcFromFile(file))) as any;
    setPreviewImage({ ...file, url: src });
  };

  // @ts-ignore
  const visibleList = fileList.filter(({ _delete }) => !_delete)

  return (
    <Style className={className}>
      { previewImage && (
        <Modal open={ !!previewImage } footer={null} onCancel={() => setPreviewImage(null)} >
          <p><Icon type="warning" /> Bilder können aktuell nicht editiert werden! Lösche das aktuelle Bild und Laden ein neues Bild hoch.<br /></p>
          <img alt="example" style={{ width: '100%' }} src={ previewImage?.url } />
        </Modal>
      )}
      <DndContext sensors={[sensor]} onDragEnd={onDragEnd}>
        <SortableContext items={fileList.map((i) => i.uid)} strategy={verticalListSortingStrategy}>
          <ImgCrop showGrid showReset aspect={aspect || 1} quality={quality || 1}>
            <Upload
              maxCount={max || 10}
              listType="picture-card"
              accept={'image/*'}
              fileList={visibleList}
              beforeUpload={handleOnBeforeUpload}
              onRemove={handleOnRemove}
              onPreview={onPreview}
              itemRender={(originNode, file) => (
                <DraggableUploadListItem originNode={originNode} file={file} />
              )}
            >
              {visibleList.length < (max || 10) && '+ Upload'}
            </Upload>
          </ImgCrop>
        </SortableContext>
      </DndContext>
    </Style>
  );
}
